import { useState } from 'react';
import { ChatText, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { DetailSection } from './DetailSection';
import { useDialog } from './useDialog';
import {
  createWorklog,
  deleteWorklog,
  updateWorklog,
  type Worklog,
  type WorklogInput,
  type WorklogList,
} from '../services/api';
import {
  formatMinutes,
  formatWorkedOn,
  minutesToInput,
  parseDuration,
  todayMadrid,
} from '../config/worklog';
import styles from '../screens/Incidencias.module.css';

/**
 * «Tiempo» del detalle del ticket (paso 93, D1 de Jose): la lista de apuntes,
 * el total en la cabecera y «Añadir tiempo» para lo que no va con un mensaje.
 * El tiempo que SÍ va con un mensaje se apunta desde el compositor del hilo.
 *
 * Es una tarjeta propia y no un bloque de «Gestión»: Gestión tiene su botón
 * que guarda estado y asignación, y un segundo formulario dentro confundía
 * qué guarda cada botón.
 *
 * Quién puede qué lo decide el backend (`canEdit` por apunte, 403 si no); aquí
 * solo se esconde lo que no se puede hacer.
 */
interface Props {
  incidenciaId: string;
  data: WorklogList | null;
  token: string;
  /** `incidencias:log_time`. */
  canLog: boolean;
  /** Ticket de un cliente desactivado (K53): se lee, no se apunta. */
  frozen: boolean;
  onChanged: () => void;
}

interface Draft {
  duration: string;
  workedOn: string;
  billable: boolean;
  note: string;
}

const EMPTY: () => Draft = () => ({ duration: '', workedOn: todayMadrid(), billable: true, note: '' });

export function WorklogSection({ incidenciaId, data, token, canLog, frozen, onChanged }: Props) {
  const { confirm, element: dialog } = useDialog();
  /** `null` = sin formulario · `'new'` = alta · id = corrigiendo ese apunte. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const items = data?.items ?? [];
  const total = data?.totalMinutes ?? 0;
  const billable = data?.billableMinutes ?? 0;
  const writable = canLog && !frozen;

  const open = (w: Worklog | null) => {
    setError(null);
    setEditing(w ? w.id : 'new');
    setDraft(w
      ? { duration: minutesToInput(w.minutes), workedOn: w.workedOn, billable: w.billable, note: w.note ?? '' }
      : EMPTY());
  };

  const save = async () => {
    const minutes = parseDuration(draft.duration);
    if (minutes === null) {
      setError('No entiendo ese tiempo. Escribe, por ejemplo, «25», «1 h» o «1h30».');
      return;
    }
    const input: WorklogInput = {
      minutes,
      workedOn: draft.workedOn || undefined,
      billable: draft.billable,
      note: draft.note.trim() || null,
    };
    setBusy(true);
    setError(null);
    try {
      if (editing === 'new') await createWorklog(incidenciaId, input, token);
      else if (editing) await updateWorklog(incidenciaId, editing, input, token);
      setEditing(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el tiempo');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (w: Worklog) => {
    const ok = await confirm({
      title: 'Borrar este apunte',
      message: `${formatMinutes(w.minutes)} del ${formatWorkedOn(w.workedOn)}, de ${w.userName ?? 'otra persona'}. El borrado queda en la auditoría.`,
      confirmLabel: 'Borrar',
      cancelLabel: 'Volver',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await deleteWorklog(incidenciaId, w.id, token);
      if (editing === w.id) setEditing(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo borrar el apunte');
    } finally {
      setBusy(false);
    }
  };

  const form = (
    <div className={styles.timeForm}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Tiempo</span>
        <input
          className={styles.input}
          value={draft.duration}
          onChange={(e) => setDraft({ ...draft, duration: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          placeholder="25, 1 h, 1h30…"
          autoFocus
        />
      </label>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Día del trabajo</span>
        <input
          type="date"
          className={styles.input}
          value={draft.workedOn}
          max={todayMadrid()}
          onChange={(e) => setDraft({ ...draft, workedOn: e.target.value })}
        />
      </label>
      <label className={`${styles.field} ${styles.timeFormWide}`}>
        <span className={styles.fieldLabel}>Nota (opcional)</span>
        <input
          className={styles.input}
          value={draft.note}
          maxLength={200}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          placeholder="Qué se hizo, si no está en el hilo"
        />
      </label>
      <div className={`${styles.timeFormWide} ${styles.timeFormFoot}`}>
        <label className={styles.composerCheck}>
          <input
            type="checkbox"
            checked={draft.billable}
            onChange={(e) => setDraft({ ...draft, billable: e.target.checked })}
          />
          Facturable
        </label>
        {/* Los dos botones van JUNTOS: si la fila no cabe (400 px), bajan los
            dos a la derecha en vez de quedar «Guardar» solo debajo. */}
        <div className={styles.timeFormButtons}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.actionButtonSecondary}`}
          onClick={() => { setEditing(null); setError(null); }}
          disabled={busy}
        >
          Cancelar
        </button>
        <button type="button" className={styles.actionButton} onClick={save} disabled={busy || !draft.duration.trim()}>
          {busy ? 'Guardando…' : 'Guardar'}
        </button>
        </div>
      </div>
    </div>
  );

  return (
    <DetailSection
      title="Tiempo"
      storageKey="incidencias.tiempo"
      right={(
        <span className={styles.timeChip} data-empty={total === 0 ? 'true' : 'false'}>
          {total === 0 ? 'Sin tiempo' : formatMinutes(total)}
        </span>
      )}
    >
      {dialog}
      {items.length === 0 && editing !== 'new' && (
        <p className={styles.timeEmpty}>Todavía no hay tiempo apuntado en este ticket.</p>
      )}
      {items.length > 0 && (
        <ul className={styles.timeList} data-actions={writable ? 'true' : 'false'}>
          {items.map((w) => (
            <li key={w.id} className={editing === w.id ? styles.timeRowEditing : styles.timeRow}>
              {editing === w.id ? form : (
                <>
                  <span className={styles.timeDate}>{formatWorkedOn(w.workedOn)}</span>
                  {/* Lo que puede crecer (etiquetas, nota) va DENTRO de esta
                      celda y no en columnas propias: así la fila es la misma
                      a 1440 y a 400, sin rejilla alternativa que mantener. */}
                  <span className={styles.timeWho}>
                    <span className={styles.timeWhoLine}>
                      <span className={styles.timeWhoName}>{w.userName ?? '—'}</span>
                      {w.messageId && (
                        <span className={styles.timeTagIcon} title="Apuntado al escribir en el hilo">
                          <ChatText size={14} weight="regular" aria-label="Con un mensaje del hilo" />
                        </span>
                      )}
                      {!w.billable && <span className={styles.timeNoBill}>No facturable</span>}
                    </span>
                    {w.note && <span className={styles.timeNote} title={w.note}>{w.note}</span>}
                  </span>
                  <span className={styles.timeMinutes}>{formatMinutes(w.minutes)}</span>
                  {writable && (
                  <span className={styles.timeRowActions}>
                    {w.canEdit && (
                      <>
                        <button
                          type="button"
                          className={styles.timeIconButton}
                          onClick={() => open(w)}
                          disabled={busy || editing !== null}
                          aria-label="Corregir el apunte"
                          title="Corregir"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          type="button"
                          className={`${styles.timeIconButton} ${styles.timeIconDanger}`}
                          onClick={() => remove(w)}
                          disabled={busy || editing !== null}
                          aria-label="Borrar el apunte"
                          title="Borrar"
                        >
                          <Trash size={14} />
                        </button>
                      </>
                    )}
                  </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {total > 0 && billable !== total && (
        <p className={styles.timeTotals}>
          Facturable: <strong>{formatMinutes(billable)}</strong> de {formatMinutes(total)}
        </p>
      )}
      {editing === 'new' && form}
      {error && <p className={styles.timeError} role="alert">{error}</p>}
      {writable && editing === null && (
        <button
          type="button"
          className={`${styles.actionButton} ${styles.actionButtonSecondary} ${styles.timeAdd}`}
          onClick={() => open(null)}
        >
          <Plus size={14} weight="bold" /> Añadir tiempo
        </button>
      )}
    </DetailSection>
  );
}
